// pages/dashboard/AdminLogin.jsx
// Steve's login page at /admin/login
// Clean, professional design matching execspringboard.com brand
// Redirects to dashboard on successful login

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // If already logged in redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      setError(result.error || 'Login failed. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Logo */}
        <div style={styles.logoArea}>
          <div style={styles.logoText}>
            <span style={styles.logoExec}>EXECUTIVE</span>
            <span style={styles.logoSpring}>Springboard</span>
          </div>
          <p style={styles.subtitle}>Admin Dashboard</p>
        </div>

        {/* Login Card */}
        <div style={styles.card}>
          <h2 style={styles.heading}>Welcome back, Steve</h2>
          <p style={styles.subheading}>Sign in to manage your assessment platform</p>

          {/* Error message */}
          {error && (
            <div style={styles.errorBox}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="form-input"
                placeholder="steve@execspringboard.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
              />
            </div>

            {/* Password */}
            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="form-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary w-full"
              style={{ marginTop: '8px' }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p style={styles.footer}>
          Executive Springboard Assessment Platform &copy; 2026
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-off-white)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  container: {
    width: '100%',
    maxWidth: '420px',
  },
  logoArea: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  logoExec: {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '3px',
    color: 'var(--color-primary)',
  },
  logoSpring: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--color-primary)',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--color-text-light)',
    marginTop: '8px',
  },
  card: {
    backgroundColor: 'var(--color-white)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-md)',
    padding: '40px',
  },
  heading: {
    fontSize: '22px',
    fontWeight: '600',
    color: 'var(--color-primary)',
    marginBottom: '8px',
  },
  subheading: {
    fontSize: '14px',
    color: 'var(--color-text-light)',
    marginBottom: '28px',
  },
  errorBox: {
    backgroundColor: '#FADBD8',
    color: '#922B21',
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
    marginBottom: '20px',
    border: '1px solid #F1948A',
  },
  footer: {
    textAlign: 'center',
    fontSize: '12px',
    color: 'var(--color-text-light)',
    marginTop: '24px',
  },
};

export default AdminLogin;
